import React, {
  forwardRef,
  useRef,
  useState,
  useEffect,
  useImperativeHandle,
} from 'react'
import classNames from 'classnames'
import { NativeProps, withNativeProps } from '../../../utils/native-props'
import { useThrottleFn } from 'ahooks'
import { mergeProps } from '../../../utils/with-default-props'
import { Sidebar } from './sidebar'
import { IndexBarContext } from './context'
import { convertPx } from '../../../utils/convert-px'

const classPrefix = `adm-index-bar`

export type IndexBarProps = {
  className?: string
  sticky?: boolean
  stickyOffsetTop?: number
  children?: React.ReactNode
} & NativeProps

export type IndexBarRef = {
  scrollTo: (index: string) => void
}

const defaultProps = {
  sticky: true,
}

export const IndexBar = forwardRef<IndexBarRef, IndexBarProps>((p, ref) => {
  // console.log("----------------- IndexBar ----------------- ")
  // console.log(p)
  const props = mergeProps(defaultProps, p)
  const titleHeight = convertPx(35)
  const bodyRef = useRef<HTMLDivElement>(null)
  const [indexes, setIndexes] = useState<string[]>([])
  const [groups, setGroups] = useState<any[]>([])
  groups.sort((a, b) => Number(a.categoryId) - Number(b.categoryId));

  const [activeIndex, setActiveIndex] = useState(indexes[0])

  useImperativeHandle(ref, () => ({ scrollTo }))

  function scrollTo(index: string) {
    const body = bodyRef.current
    if (!body) return

    const children = body.children
    for (let i = 0; i < children.length; i++) {
      const panel = children.item(i) as HTMLElement
      if (!panel) continue
      const panelIndex = panel.dataset['index']
      if (panelIndex == index) {
        body.scrollTop = panel.offsetTop
        setActiveIndex(index)
        return
      }
    }
  }

  const { run: checkActiveIndex } = useThrottleFn(
    () => {
      const body = bodyRef.current
      if (!body) return
      const scrollTop = body.scrollTop

      const children = body.getElementsByClassName(`${classPrefix}-anchor`)
      for (let i = 0; i < children.length; i++) {
        const panel = children.item(i) as HTMLElement
        if (!panel) continue
        const panelIndex = panel.dataset['index']
        if (!panelIndex) continue
        if (panel.offsetTop + panel.clientHeight - titleHeight > scrollTop) {
          setActiveIndex(panelIndex)
          return
        }
      }
    },
    { wait: 50, trailing: true, leading: true }
  )

  useEffect(() => {
    if (!indexes.length) return
    checkActiveIndex()
  }, [indexes])

  let stickyStyle: any
  if (props.stickyOffsetTop) {
    stickyStyle = {
      '--sticy-offset-top': props.stickyOffsetTop + 'px',
    }
  }

  const element = withNativeProps(
    props,
    <div
      className={classNames(`${classPrefix}`, {
        [`${classPrefix}-sticky`]: props.sticky,
      })}
      style={stickyStyle}
    >
      <Sidebar      
        groups={groups}
        indexes={indexes}
        activeIndex={activeIndex}
        onActive={index => {
          scrollTo(index)
        }}
      />

      <div
        className={`${classPrefix}-body`}
        ref={bodyRef}
        onScroll={checkActiveIndex}
      >
        {props.children}
      </div>
    </div>
  )

  return (
    <IndexBarContext.Provider
      value={{
        indexes,
        setIndexes,
        groups,
        setGroups,
      }}
    >
      {element}
    </IndexBarContext.Provider>
  )
})
